import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { DataView } from "primereact/dataview";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { collection, getDocs } from "firebase/firestore";
import { db, getFirebaseImageUrl } from "../firebase";
import galleryImage from "../assets/img/gallery.img/gallery.webp";
import { amount } from "CustomMath";
import { useTranslation } from "language-maps/use-translation";
import { getDesignDiscountsMap } from "../designDiscountService";
import { getActiveDiscount } from "../discountService";
import {
  slugToName,
  nameToSlug,
  mainCategories,
  blindSubCategories,
  blindSubCategorySlugs,
} from "../categoryMappings";
import blindDropdownImg from "../assets/img/home.img/home-image-1.jpg";
import "../assets/css/gallery.css";

const Gallery = () => {
  const { category: categorySlug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortKey, setSortKey] = useState("!order");
  const [sortOrder, setSortOrder] = useState(-1);
  const [sortField, setSortField] = useState("order");
  const [searchQuery, setSearchQuery] = useState("");
  const [discountsMap, setDiscountsMap] = useState({});
  const [commonDiscount, setCommonDiscount] = useState(null);
  const { t } = useTranslation();

  const isBlindSubCategory = categorySlug && blindSubCategorySlugs.includes(categorySlug);
  const categoryName = categorySlug ? slugToName[categorySlug] || "All" : "All";

  const sortOptions = [
    { label: t("Added: New to Old"), value: "!order" },
    { label: t("Added: Old to New"), value: "order" },
    { label: t("Price: High to Low"), value: "!price" },
    { label: t("Price: Low to High"), value: "price" },
  ];

  useEffect(() => {
    getActiveDiscount().then((d) => setCommonDiscount(d));
  }, []);

  useEffect(() => {
    const loadData = async () => {
      const data = [];
      const querySnapshot = await getDocs(collection(db, "Designs"));

      querySnapshot.forEach((doc) => {
        if (doc.exists && doc.data().Status) {
          const record = {
            id: doc.id,
            code: doc.data().Code,
            name: doc.data().Name,
            description: doc.data().Description,
            image: doc.data().Image,
            price: doc.data().Price,
            order: doc.data().Order || 0,
            category: doc.data().Category || "",
            subCategory: doc.data().SubCategory || "",
          };
          data.push(record);
        }
      });
      setProducts(data);
      const ids = data.map((p) => p.id);
      try {
        const map = await getDesignDiscountsMap(ids);
        setDiscountsMap(map);
      } catch {
        setDiscountsMap({});
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    applyFilters(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, categorySlug]);

  const onSortChange = (event) => {
    const value = event.value;
    setSortOrder(value.startsWith("!") ? -1 : 1);
    setSortField(value.startsWith("!") ? value.substring(1) : value);
    setSortKey(value);
  };

  const onSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    applyFilters(query);
  };

  const onCategoryChange = (cat) => {
    if (cat === "Blinds") {
      // Blinds tab opens the dedicated Blinds landing page
      navigate("/blinds");
      return;
    }
    const slug = nameToSlug[cat] || "all";
    navigate(`/gallery/${slug}`);
  };

  const applyFilters = (query) => {
    let filtered = products;

    if (isBlindSubCategory) {
      filtered = filtered.filter((p) => p.subCategory === categorySlug);
    } else if (categorySlug && categorySlug !== "all") {
      const name = slugToName[categorySlug];
      if (name) {
        filtered = filtered.filter((p) => p.category === name);
      }
    }

    if (query) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.code.toLowerCase().includes(query)
      );
    }
    setFilteredProducts(filtered);
  };

  const isBlindsCategory = (item) => {
    return (
      item.category === "Blinds" ||
      blindSubCategorySlugs.includes(item.subCategory)
    );
  };

  const getDiscountLabel = (discount) => {
    if (!discount || (discount.value === 0 && discount.value !== undefined)) return null;
    const value = discount.value;
    if (value === undefined || value === null) return null;
    if (discount.isPercentage) {
      const display = value % 1 === 0 ? value : parseFloat(value.toFixed(1));
      return `${display}% off the total`;
    }
    return amount(value);
  };

  const renderGridItem = (data) => {
    const effectiveDiscount = discountsMap[data.id] ?? commonDiscount;
    const discountLabel = getDiscountLabel(effectiveDiscount);
    // Interior Designs are finished, fixed-size products sold per item.
    const priceUnit =
      data.category === "Interior Designs"
        ? t("Per Item")
        : isBlindsCategory(data)
        ? t("Per Ft")
        : t("Per Meter");
    return (
      <div className="col-6 lg:col-3 p-2">
        <Link to={`/item/${data.id}`} className="grid-item">
          <span className="gallery-item-image-wrap">
            <img
              alt={t("Image not found")}
              src={getFirebaseImageUrl(data.image)}
            />
            {discountLabel && (
              <span className="gallery-offer-banner">
                <span className="offer-banner-label">{commonDiscount?.name || "SPECIAL OFFER"}</span>
                <span className="offer-banner-value">{discountLabel}</span>
              </span>
            )}
          </span>
          <div className="flex flex-column align-items-center gap-1">
            <label className="w-full ellipsis text-center cormorant-garamond text-secondary text-xl">
              {data.name}
            </label>
            <label className="w-full ellipsis text-center text-sm text-primary">
              <strong>{amount(data.price)} LKR</strong> - {priceUnit}
            </label>
            <label className="w-full ellipsis text-center text-sm text-primary">
              {data.code}
            </label>
          </div>
        </Link>
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div className="grid mb-4">
        <div className="col-6">
          <Dropdown
            options={sortOptions}
            value={sortKey}
            optionLabel="label"
            placeholder={t("Sort By Price")}
            onChange={onSortChange}
            style={{ maxWidth: "100%" }}
          />
        </div>
        <div className="col-6 text-right">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              className="w-full"
              placeholder={t("Search")}
              value={searchQuery}
              onChange={onSearchChange}
            />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-column mb-6">
      <div
        className="bg-top bg-no-repeat flex flex-column align-items-center pt-7 page-image-header"
        style={{ backgroundImage: `url(${galleryImage})` }}
      >
        <h1 className="text-primary cormorant-garamond font-bold text-10xl">
          Gallery
        </h1>
        <h1 className="lg:text-8xl text-6xl lg:mt-5 mt-2 text-elegance">
          of Elegance
        </h1>
        <p className="text-primary lg:text-2xl text-lg text-center lg:mt-6 mt-4 gallery-p">
          {t("Explore our wide range of latest curtain designs that will match all your needs at the most affordable price in Sri Lanka")}
        </p>
      </div>
      <div className="content-wrapper gallery mt-8">
        {!isBlindSubCategory && (
          <div className="gallery-category-tabs">
            {mainCategories.map((cat) => (
              <div
                key={cat}
                className={`gallery-category-tab-wrap${cat === "Blinds" ? " has-dropdown" : ""}`}
              >
                <button
                  className={`gallery-category-tab${categoryName === cat ? " active" : ""}`}
                  onClick={() => onCategoryChange(cat)}
                >
                  {t(cat)}
                </button>
                {cat === "Blinds" && (
                  <div className="gallery-blinds-dropdown">
                    <div className="blinds-dropdown-links">
                      {blindSubCategories.map((sub) => (
                        <Link
                          key={sub}
                          to={`/gallery/${nameToSlug[sub]}`}
                          className="blinds-dropdown-link"
                        >
                          {t(sub)} <i className="pi pi-external-link" style={{ fontSize: "0.7rem" }} />
                        </Link>
                      ))}
                    </div>
                    <div className="blinds-dropdown-image">
                      <img src={blindDropdownImg} alt="Blinds" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {isBlindSubCategory && (
          <div className="gallery-subcategory-header">
            <button
              className="gallery-back-button"
              onClick={() => navigate("/gallery/blinds")}
            >
              <i className="pi pi-angle-left" /> {t("Back to Blinds")}
            </button>
            <h2 className="cormorant-garamond">{t(categoryName)}</h2>
          </div>
        )}
        <DataView
          value={filteredProducts}
          layout={"grid"}
          header={renderHeader()}
          itemTemplate={renderGridItem}
          paginator
          rows={48}
          sortOrder={sortOrder}
          sortField={sortField}
          gutter
        />
      </div>
    </div>
  );
};

export default Gallery;
